import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { CloseOutlined, EditOutlined, MinusOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';
import { collect } from 'collect.js';
import useHelpers from '../../hooks/context/useHelpers';
import { SubscriptionProductType, TAX_BEHAVIOUR } from '../../services/exports/Constants';
import TaxCalculator from '../../services/helpers/TaxCalculator';
import { computeSubscriptionTax, resolveUnitAmount } from '../../services/helpers/PricingHelpers';
import { discountPrice, discountPriceTiers, isPlanType } from '../../services/helpers/ContractDiscountHelpers';
import usePermissions, { ManagerPermission } from '../../hooks/auth/usePermissions';
import useForm from '../../hooks/utility/useForm';
import PriceEditor from '../subscription/PriceEditor';
import PricingTiers from '../subscription/PricingTiers';

export const VARIANTS = {
  primary: 'primary',
  secondary: 'secondary',
};

function EditView({ data, localData, price, variant, update, onUpdate, setIsEditing }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:SubscriptionProduct' });

  const _onUpdate = () => {
    onUpdate({
      price: {
        ...localData,
        ...computeSubscriptionTax(localData.amount, price.tax_percentage, localData.tax_behaviour),
        tiers: localData.has_pricing_tiers ? localData.tiers : [],
      },
    });
    setIsEditing(false);
  };

  return (
    <div className="rotate-y-180">
      <Button icon={<CloseOutlined />} onClick={() => setIsEditing(false)} className="absolute right-0 top-0" />
      <h5>{data.name}</h5>
      {data?.description && <p className="mb-0 whitespace-pre-line">{data?.description}</p>}
      <PriceEditor data={localData} disableTiers={variant === VARIANTS.secondary} update={update} />
      <Button type="primary" size="large" onClick={_onUpdate}>
        {t('buttons.save')}
      </Button>
    </div>
  );
}

function Preview({
  data,
  lineItem,
  prevState,
  variant,
  update,
  onAdd,
  onUpdate,
  onRemove,
  setIsEditing,
  disabled,
  discount = 0,
  readOnly = false,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:SubscriptionProduct' });

  const { can } = usePermissions();
  const { formatCurrency } = useHelpers();

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const quantity = lineItem?.quantity ?? 0;
  const isPrimary = variant === VARIANTS.primary;
  const isAdded = quantity > 0;

  const price = lineItem?.price ?? data.price;
  // Reflect the annual-contract discount in the displayed prices/tiers. A
  // plan's base fee (tier 0, or its whole flat price) is never discounted —
  // only its success-based tiers and standalone add-ons are.
  const isPlan = isPlanType(data.type);
  const displayPrice = useMemo(() => discountPrice(price, discount, !isPlan), [price, discount, isPlan]);
  const displayTiers = useMemo(() => discountPriceTiers(data.price.tiers, discount), [data.price.tiers, discount]);
  const { amount } = displayPrice;
  const unitAmount = useMemo(() => resolveUnitAmount(displayPrice), [displayPrice]);

  const defaultPrice = useMemo(() => collect(data.prices).firstWhere('is_default', true), [data.prices]);
  const hasDefaultPrice = price.id === defaultPrice?.id;

  const onSetDefaultPrice = () =>
    onUpdate({
      price: defaultPrice,
    });

  const undoChanges = () => {
    onUpdate(prevState);
    update('has_pricing_tiers', prevState.price.tiers.length > 0);
  };

  const renderTax = () => {
    const taxCalculator = TaxCalculator.make(price.tax_behaviour);
    const tax =
      price.tiers.length > 0
        ? t('labels.varies_by_tier')
        : formatCurrency(taxCalculator.tax(amount, price.tax_percentage), price.currency);

    if (price.tax_behaviour === TAX_BEHAVIOUR.inclusive) {
      return t('tax.inclusive', {
        taxName: serviceProvider?.regional_config?.tax_name ?? 'VAT',
        tax,
      });
    }

    return t('tax.exclusive', {
      taxName: serviceProvider?.regional_config?.tax_name ?? 'VAT',
      tax,
    });
  };

  return (
    <>
      {quantity > 0 && (
        <Tag color="green" className="mb-xs w-fit">
          ADDED
        </Tag>
      )}
      <h3 className="leading-[2rem] font-semibold">{data.name}</h3>
      {data?.description && <p className="mb-0 whitespace-pre-line">{data?.description}</p>}
      <span className="inline-flex">
        <h2 className="leading-[2.25rem] font-semibold">{formatCurrency(unitAmount, price.currency)}</h2>
        <span className="mt-auto ml-xs h-fit text-gray-500">/{t('labels.month')}</span>
      </span>
      <PricingTiers data={data.price.tiers} discount={discount} className="space-y-xs" />
      <p className="text-sm text-gray-500">{renderTax()}</p>
      {price.has_pricing_tiers && data.price.tiers.length > 0 && (
        <p className="w-full text-center text-sm text-gray-500">
          {t('labels.max_price', { amount: formatCurrency(collect(displayTiers).last().amount) })}
        </p>
      )}
      {!readOnly && isPrimary && !isAdded && (
        <Button type="primary" size="large" onClick={onAdd} disabled={disabled}>
          {t('buttons.select')}
        </Button>
      )}
      {!readOnly && (!isPrimary || isAdded) && (
        <div className="border border-solid border-brand-inkGrey-grey_2 rounded-lg py-2xs px-md w-fit flex justify-between items-center">
          <MinusOutlined className="my-auto cursor-pointer text-lg" onClick={disabled ? () => null : onRemove} />
          <h5 className="mx-lg my-auto select-none">{quantity}</h5>
          <PlusOutlined className="my-auto cursor-pointer text-lg" onClick={disabled ? () => null : onAdd} />
        </div>
      )}
      {!readOnly &&
        can(
          data.type === SubscriptionProductType.Plan
            ? ManagerPermission.EditSubscriptionPlanPrices
            : ManagerPermission.EditSubscriptionPrices,
        ) && (
          <div className="absolute right-4 top-4 !mt-none flex gap-x-xs">
            {Boolean(defaultPrice) && !hasDefaultPrice && (
              <Button color="default" variant="solid" onClick={onSetDefaultPrice}>
                {t('buttons.use_default_price')}
              </Button>
            )}
            {data.price.id !== prevState.price.id && (
              <Button icon={<UndoOutlined />} onClick={undoChanges} className="mr-2" />
            )}
            <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)} />
          </div>
        )}
    </>
  );
}

function SubscriptionProduct({
  data,
  lineItem,
  prevState,
  variant = VARIANTS.primary,
  disabled,
  discount = 0,
  readOnly = false,
  onAdd,
  onUpdate,
  onRemove,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const { data: localData, update } = useForm({
    ...data.price,
    id: null,
    tax_behaviour: TAX_BEHAVIOUR.exclusive,
    amount: data.price.amount,
    tiers: data.price.tiers ?? [],
    has_pricing_tiers: data.price.tiers.length > 0,
  });

  const quantity = lineItem?.quantity ?? 0;
  const isPrimary = variant === VARIANTS.primary;
  const isSecondary = variant === VARIANTS.secondary;
  const isAdded = quantity > 0;

  const price = lineItem?.price ?? data.price;

  const props = {
    data,
    localData,
    lineItem,
    prevState,
    price,
    variant,
    update,
    onAdd,
    onUpdate,
    onRemove,
    setIsEditing,
    disabled,
    discount,
    readOnly,
  };

  return (
    <div
      className={classnames(
        'col-span-2 lg:col-span-1 bg-white shadow rounded-lg p-md flex flex-col relative duration-1000 origin-center-right transform-style-3d space-y-md',
        {
          shadow: isPrimary,
          'border justify-center': isSecondary,
          'border-primary': isSecondary && isAdded,
          '-translate-x-full -rotate-y-180': isEditing,
        },
      )}
    >
      {isEditing ? <EditView {...props} /> : <Preview {...props} />}
    </div>
  );
}

export default SubscriptionProduct;
